import { FormEntry } from "../../../types/types";
import { formMappings } from "../../../config/formMappings";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { useEffect, useState } from "react";
import { getFormEntries } from "../../../services/apiService";
import { getRegistrantsContainer } from "../../../utils/utils";

// Helper function to extract emails from an entry
function getEmailsFromEntry(
  entry: FormEntry,
  activeFormName: string | null
): string[] {
  const emails: string[] = [];

  // First, check if Email exists at entry level
  if (entry.meta.Email) {
    if (Array.isArray(entry.meta.Email)) {
      emails.push(...entry.meta.Email.map((e) => String(e).toLowerCase()));
    } else {
      emails.push(String(entry.meta.Email).toLowerCase());
    }
  }

  // Also extract emails from registrants
  const registrantsContainer = getRegistrantsContainer(entry.meta);
  if (registrantsContainer) {
    // Get email field key from form mapping
    const formMapping = activeFormName
      ? formMappings[activeFormName as keyof typeof formMappings]
      : null;
    const emailFieldKey = formMapping?.fieldMappings?.email || "jgfy3";

    Object.entries(registrantsContainer)
      .filter(([key]) => key !== "form")
      .forEach(([_, registrant]: [string, any]) => {
        const email = registrant[emailFieldKey];
        if (email) {
          // Handle both string and array email values
          if (Array.isArray(email)) {
            emails.push(...email.map((e) => String(e).toLowerCase()));
          } else {
            emails.push(String(email).toLowerCase());
          }
        }
      });
  }

  return emails;
}

export default function TransportationRequestCard(props: { entry: FormEntry }) {
  const activeFormName = useSelector(
    (state: RootState) => state.activeForm.activeFormName
  );
  const formMapping = formMappings[activeFormName as keyof typeof formMappings];
  const transportationRequestMapping =
    formMapping && "transportationRequest" in formMapping
      ? formMapping.transportationRequest
      : undefined;

  const [transportationRequests, setTransportationRequests] = useState<
    FormEntry[]
  >([]);
  const [loading, setLoading] = useState(false);

  // Fetch transportation requests and match by email
  useEffect(() => {
    // Extract emails from the entry
    const entryEmails = getEmailsFromEntry(props.entry, activeFormName);

    if (!transportationRequestMapping || entryEmails.length === 0) {
      setTransportationRequests([]);
      return;
    }

    setLoading(true);
    getFormEntries(transportationRequestMapping.formId)
      .then((data) => {
        // Match transportation requests by email (case-insensitive)
        const matchingRequests = data.filter((trEntry: FormEntry) => {
          if (!transportationRequestMapping) return false;

          const trEmailRaw =
            trEntry.meta[transportationRequestMapping.fieldMappings.email] ||
            trEntry.meta["Email"] ||
            "";
          const trEmail = Array.isArray(trEmailRaw)
            ? String(trEmailRaw[0]).toLowerCase()
            : String(trEmailRaw).toLowerCase();

          return entryEmails.includes(trEmail);
        });

        // Filter and prioritize: show paid entries, or if all unpaid, show most recent
        let filteredRequests: FormEntry[] = [];

        if (matchingRequests.length === 0) {
          filteredRequests = [];
        } else if (matchingRequests.length === 1) {
          filteredRequests = matchingRequests;
        } else {
          // Check for paid entries
          const paidRequests = matchingRequests.filter((trEntry: FormEntry) => {
            if (!transportationRequestMapping) return false;
            const paidStatusRaw =
              trEntry.meta[transportationRequestMapping.fieldMappings.paid] ||
              trEntry.meta["Paid?"] ||
              "No";
            const paidStatus = Array.isArray(paidStatusRaw)
              ? paidStatusRaw[0]
              : paidStatusRaw;
            return paidStatus === "Yes";
          });

          if (paidRequests.length > 0) {
            // If there are paid entries, show only the most recent paid one
            paidRequests.sort(
              (a: FormEntry, b: FormEntry) =>
                new Date(b.created_at).getTime() -
                new Date(a.created_at).getTime()
            );
            filteredRequests = [paidRequests[0]];
          } else {
            // If all are unpaid, show only the most recent one
            matchingRequests.sort(
              (a: FormEntry, b: FormEntry) =>
                new Date(b.created_at).getTime() -
                new Date(a.created_at).getTime()
            );
            filteredRequests = [matchingRequests[0]];
          }
        }

        setTransportationRequests(filteredRequests);
      })
      .catch((error) => {
        console.error("Error fetching transportation requests:", error);
        setTransportationRequests([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [transportationRequestMapping, props.entry, activeFormName]);

  if (!transportationRequestMapping) {
    return null;
  }

  if (loading) {
    return (
      <section aria-labelledby="transportation-requests-title">
        <div className="overflow-hidden bg-white shadow rounded-lg">
          <div className="px-4 py-6 sm:px-6">
            <h3 className="text-base/7 font-semibold text-gray-900">
              Transportation Requests
            </h3>
            <p className="mt-1 max-w-2xl text-sm/6 text-gray-500">
              Loading transportation request details...
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (transportationRequests.length === 0) {
    return (
      <section aria-labelledby="transportation-requests-title">
        <div className="overflow-hidden bg-white shadow rounded-lg">
          <div className="px-4 py-6 sm:px-6">
            <h3 className="text-base/7 font-semibold text-gray-900">
              Transportation Requests
            </h3>
            <p className="mt-1 max-w-2xl text-sm/6 text-gray-500">
              No transportation requests found for this registrant.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section aria-labelledby="transportation-requests-title">
      <div className="overflow-hidden bg-white shadow rounded-lg">
        <div className="px-4 py-6 sm:px-6">
          <h3 className="text-base/7 font-semibold text-gray-900">
            Transportation Requests
          </h3>
          <p className="mt-1 max-w-2xl text-sm/6 text-gray-500">
            Transportation request details for this registrant.
          </p>
        </div>
        <div className="border-t border-gray-100">
          {transportationRequests.map((trEntry, idx) => {
            const firstNameRaw =
              trEntry.meta[
                transportationRequestMapping.fieldMappings.firstName
              ] ||
              trEntry.meta["First Name"] ||
              "";
            const lastNameRaw =
              trEntry.meta[
                transportationRequestMapping.fieldMappings.lastName
              ] ||
              trEntry.meta["Last Name"] ||
              "";
            const emailRaw =
              trEntry.meta[transportationRequestMapping.fieldMappings.email] ||
              trEntry.meta["Email"] ||
              "";
            const phoneRaw =
              trEntry.meta[transportationRequestMapping.fieldMappings.phone] ||
              trEntry.meta["Phone"] ||
              "";
            const numberOfPeopleRaw =
              trEntry.meta[
                transportationRequestMapping.fieldMappings.numberOfPeople
              ] || "";
            const luggageCountRaw =
              trEntry.meta[
                transportationRequestMapping.fieldMappings.luggageCount
              ] || "";
            const needCarSeatRaw =
              trEntry.meta[
                transportationRequestMapping.fieldMappings.needCarSeat
              ] || "";
            const arrivalDateRaw =
              trEntry.meta[
                transportationRequestMapping.fieldMappings.arrivalDate
              ] || "";
            const arrivalTimeRaw =
              trEntry.meta[
                transportationRequestMapping.fieldMappings.arrivalTime
              ] || "";
            const arrivalFlightNumberRaw =
              trEntry.meta[
                transportationRequestMapping.fieldMappings.arrivalFlightNumber
              ] || "";
            const departureDateRaw =
              trEntry.meta[
                transportationRequestMapping.fieldMappings.departureDate
              ] || "";
            const departureTimeRaw =
              trEntry.meta[
                transportationRequestMapping.fieldMappings.departureTime
              ] || "";
            const departureFlightNumberRaw =
              trEntry.meta[
                transportationRequestMapping.fieldMappings.departureFlightNumber
              ] || "";
            const totalCostRaw =
              trEntry.meta[
                transportationRequestMapping.fieldMappings.totalCost
              ] || "";
            const paidStatusRaw =
              trEntry.meta[transportationRequestMapping.fieldMappings.paid] ||
              trEntry.meta["Paid?"] ||
              "No";

            // Handle array values
            const firstName = Array.isArray(firstNameRaw)
              ? firstNameRaw[0]
              : firstNameRaw;
            const lastName = Array.isArray(lastNameRaw)
              ? lastNameRaw[0]
              : lastNameRaw;
            const email = Array.isArray(emailRaw) ? emailRaw[0] : emailRaw;
            const phone = Array.isArray(phoneRaw) ? phoneRaw[0] : phoneRaw;
            const numberOfPeople = Array.isArray(numberOfPeopleRaw)
              ? numberOfPeopleRaw[0]
              : numberOfPeopleRaw;
            const luggageCount = Array.isArray(luggageCountRaw)
              ? luggageCountRaw[0]
              : luggageCountRaw;
            const needCarSeat = Array.isArray(needCarSeatRaw)
              ? needCarSeatRaw[0]
              : needCarSeatRaw;
            const arrivalDate = Array.isArray(arrivalDateRaw)
              ? arrivalDateRaw[0]
              : arrivalDateRaw;
            const arrivalTime = Array.isArray(arrivalTimeRaw)
              ? arrivalTimeRaw[0]
              : arrivalTimeRaw;
            const arrivalFlightNumber = Array.isArray(arrivalFlightNumberRaw)
              ? arrivalFlightNumberRaw[0]
              : arrivalFlightNumberRaw;
            const departureDate = Array.isArray(departureDateRaw)
              ? departureDateRaw[0]
              : departureDateRaw;
            const departureTime = Array.isArray(departureTimeRaw)
              ? departureTimeRaw[0]
              : departureTimeRaw;
            const departureFlightNumber = Array.isArray(
              departureFlightNumberRaw
            )
              ? departureFlightNumberRaw[0]
              : departureFlightNumberRaw;
            const totalCost = Array.isArray(totalCostRaw)
              ? totalCostRaw[0]
              : totalCostRaw;
            const paidStatus = Array.isArray(paidStatusRaw)
              ? paidStatusRaw[0]
              : paidStatusRaw;

            return (
              <div key={trEntry.id || idx}>
                {idx > 0 && <div className="border-t border-gray-200"></div>}
                <dl className="divide-y divide-gray-100">
                  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">Name</dt>
                    <dd className="mt-1 text-lg/6 text-gray-700 sm:col-span-2 sm:mt-0">
                      {firstName} {lastName}
                    </dd>
                  </div>
                  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">Email</dt>
                    <dd className="mt-1 text-lg/6 text-gray-700 sm:col-span-2 sm:mt-0">
                      {email ? (
                        <a
                          href={`mailto:${email}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary hover:text-secondary"
                        >
                          {email}
                        </a>
                      ) : (
                        "N/A"
                      )}
                    </dd>
                  </div>
                  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">Phone</dt>
                    <dd className="mt-1 text-lg/6 text-gray-700 sm:col-span-2 sm:mt-0">
                      {phone ? (
                        <a
                          href={`tel:${phone}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary hover:text-secondary"
                        >
                          {phone}
                        </a>
                      ) : (
                        "N/A"
                      )}
                    </dd>
                  </div>
                  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">
                      Number of People
                    </dt>
                    <dd className="mt-1 text-lg/6 text-gray-700 sm:col-span-2 sm:mt-0">
                      {numberOfPeople || "N/A"}
                    </dd>
                  </div>
                  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">
                      Number of Luggage
                    </dt>
                    <dd className="mt-1 text-lg/6 text-gray-700 sm:col-span-2 sm:mt-0">
                      {luggageCount || "N/A"}
                    </dd>
                  </div>
                  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">
                      Need Car Seat
                    </dt>
                    <dd className="mt-1 text-lg/6 text-gray-700 sm:col-span-2 sm:mt-0">
                      {needCarSeat || "N/A"}
                    </dd>
                  </div>
                  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">
                      Arrival Date
                    </dt>
                    <dd className="mt-1 text-lg/6 text-gray-700 sm:col-span-2 sm:mt-0">
                      {arrivalDate || "N/A"}
                    </dd>
                  </div>
                  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">
                      Arrival Time
                    </dt>
                    <dd className="mt-1 text-lg/6 text-gray-700 sm:col-span-2 sm:mt-0">
                      {arrivalTime || "N/A"}
                    </dd>
                  </div>
                  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">
                      Arrival Flight Number
                    </dt>
                    <dd className="mt-1 text-lg/6 text-gray-700 sm:col-span-2 sm:mt-0">
                      {arrivalFlightNumber || "N/A"}
                    </dd>
                  </div>
                  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">
                      Departure Date
                    </dt>
                    <dd className="mt-1 text-lg/6 text-gray-700 sm:col-span-2 sm:mt-0">
                      {departureDate || "N/A"}
                    </dd>
                  </div>
                  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">
                      Departure Time
                    </dt>
                    <dd className="mt-1 text-lg/6 text-gray-700 sm:col-span-2 sm:mt-0">
                      {departureTime || "N/A"}
                    </dd>
                  </div>
                  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">
                      Departure Flight Number
                    </dt>
                    <dd className="mt-1 text-lg/6 text-gray-700 sm:col-span-2 sm:mt-0">
                      {departureFlightNumber || "N/A"}
                    </dd>
                  </div>
                  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">
                      Total Cost
                    </dt>
                    <dd className="mt-1 text-lg/6 text-gray-700 sm:col-span-2 sm:mt-0">
                      {totalCost
                        ? `$${parseFloat(String(totalCost)).toFixed(2)}`
                        : "N/A"}
                    </dd>
                  </div>
                  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">Paid</dt>
                    <dd className="mt-1 text-lg/6 text-gray-700 sm:col-span-2 sm:mt-0">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                          paidStatus === "Yes"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {paidStatus === "Yes" ? "Yes" : "No"}
                      </span>
                    </dd>
                  </div>
                </dl>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
